import { TranslationsContext } from '@teliaee/telia-translate';
import * as React from 'react';
import {
  AnalyticsProvider,
  AnalyticsTrigger,
  Button,
  Card,
  CardContent,
  Grid,
  GridColumn,
  Heading,
  Modal,
  ModalContent,
  ModalProvider,
  ModalTrigger,
  Notice,
  NumberField,
  Separator,
  Table,
  TableCell,
  TableRow,
  Text,
  THeadingLevel,
} from 'telia-front-react';
import GravityForm from '../forms/GravityForm';
import { formatPrice, renderCurrency } from '../util/PriceUtil';

export interface CyberSecurityCalculatorProps {
  prices: CyberSecurityParam[];
  successFormName: string;
  mainHeadingLevel?: THeadingLevel;
  headingLevel?: THeadingLevel;
}

type Interval = [number, number?];

export interface CyberSecurityParam {
  employees: Interval;
  perEmployee: number | 'deal';
  consultation?: number | 'deal';
}

const MIN_EMPLOYEE_COUNT: number = 2;

const CyberSecurityCalculator: React.FC<CyberSecurityCalculatorProps> = ({ prices, successFormName, mainHeadingLevel = 2, headingLevel = 3 }) => {
  const translate = React.useContext(TranslationsContext).translate;
  const [employees, setEmployees] = React.useState(MIN_EMPLOYEE_COUNT);

  const param = prices.find((price) => price.employees[0] <= employees && (employees <= price.employees[1] || !price.employees[1]));
  const price = param.perEmployee === 'deal' ? 'deal' : param.perEmployee * employees + (typeof param.consultation === 'number' ? param.consultation : 0);

  const calculationModal = (
    <AnalyticsTrigger
      trigger={{
        id: ([isOpen]) => `Link - ${isOpen ? 'open' : 'close'} Modal Küberturvalisuse Hinnad`,
        callback: 'onToggle',
      }}
    >
      <ModalProvider id={'cyber-security-prices-modal'}>
        <p className={'text-center'}>
          {translate('cyberSecurity.info')}{' '}
          <ModalTrigger>
            <Button id={'calculation-button'} link={true} text={translate('cyberSecurity.openInfo')} />
          </ModalTrigger>
        </p>
        <Modal size={'10-col'}>
          <ModalContent>
            <Grid>
              <GridColumn width={['xs-12', 'md-6']}>
                <Heading level={3}>{translate('cyberSecurity.modal.title')}</Heading>
              </GridColumn>
            </Grid>
            <Separator spacing={'xxsmall'} color={'invisible'} />
            <p>{translate('cyberSecurity.modal.subText')}</p>
            <Separator spacing={'xsmall'} color={'invisible'} />
            <Table>
              <thead>
                <TableRow>
                  <TableCell type={'th'} scope={'col'}>
                    {translate('cyberSecurity.modal.employeeCount')}
                  </TableCell>
                  <TableCell type={'th'} scope={'col'}>
                    {translate('cyberSecurity.modal.pricePerEmployee')}
                  </TableCell>
                  <TableCell type={'th'} scope={'col'}>
                    {translate('cyberSecurity.modal.consultationPrice')}
                  </TableCell>
                </TableRow>
              </thead>
              <tbody>
                {prices.map((p, idx) => (
                  <TableRow key={idx}>
                    <TableCell type={'td'}>
                      {p.employees[0]}
                      {p.employees[1] ? ` - ${p.employees[1]}` : '+'}
                    </TableCell>
                    <TableCell type={'td'}>
                      {typeof p.perEmployee === 'number' ? `${formatPrice(p.perEmployee)} €` : translate('cyberSecurity.onAgreement')}
                    </TableCell>
                    <TableCell type={'td'}>
                      {typeof p.consultation === 'number'
                        ? `${p.consultation} €`
                        : p.consultation === undefined
                          ? translate('cyberSecurity.notOfferable')
                          : translate('cyberSecurity.onAgreement')}
                    </TableCell>
                  </TableRow>
                ))}
              </tbody>
            </Table>
            <Separator spacing={'xsmall'} color={'invisible'} />
            <Notice size={'small'}>{translate('cyberSecurity.vatAdded')}</Notice>
          </ModalContent>
        </Modal>
      </ModalProvider>
    </AnalyticsTrigger>
  );

  return (
    <AnalyticsProvider group="Küberturvalisuse Kalkulaator">
      <Card padding={'medium'}>
        <CardContent>
          <Heading level={mainHeadingLevel} className={'h2 text-center'}>
            {translate('cyberSecurity.title')}
          </Heading>
          {calculationModal}
          <Separator spacing={'xsmall'} color={'invisible'} />
          <Grid>
            <GridColumn width={['sm-6']}>
              <Text className="text-center">
                <Heading level={headingLevel} className="h4">
                  {translate('cyberSecurity.employees')}
                </Heading>
                <NumberField
                  label={translate('cyberSecurity.employees')}
                  id={'employee-count-number'}
                  min={MIN_EMPLOYEE_COUNT}
                  max={9999}
                  defaultValue={MIN_EMPLOYEE_COUNT}
                  onChange={setEmployees}
                  align="center"
                  decreaseButtonText={translate('cyberSecurity.employees.decrease')}
                  increaseButtonText={translate('cyberSecurity.employees.increase')}
                />
              </Text>
            </GridColumn>
            <GridColumn width={['sm-6']}>
              <Text className="text-center">
                <Heading level={headingLevel} className="h4">
                  {translate('cyberSecurity.totalPayment')}
                </Heading>
                <p className="h5 text-bold">
                  {price === 'deal'
                    ? translate('cyberSecurity.onAgreement')
                    : `${formatPrice(price)} ${renderCurrency(false, true, translate, '€', `${translate('cyberSecurity.perYear')}`)}`}
                </p>
                <AnalyticsTrigger
                  trigger={{
                    id: ([isOpen]) => `Link - ${isOpen ? 'open' : 'close'} Modal Küberturvalisuse Tellimine`,
                    callback: 'onToggle',
                  }}
                >
                  <ModalProvider id={'cyber-security-order-modal'}>
                    <ModalTrigger>
                      <Button id={'cyber-security-order-btn'} text={translate('cyberSecurity.order')} type="expressive" iconRight="#arrow-right" />
                    </ModalTrigger>
                    <Modal size={'8-col'}>
                      <ModalContent>
                        <GravityForm name={successFormName} />
                      </ModalContent>
                    </Modal>
                  </ModalProvider>
                </AnalyticsTrigger>
              </Text>
            </GridColumn>
          </Grid>
        </CardContent>
      </Card>
    </AnalyticsProvider>
  );
};

export default CyberSecurityCalculator;
